import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { buildAppMeta } from "@/lib/seo";
import { fmtBookingDate, fmtSlotTime } from "@/lib/booking";
import {
  getManagedBooking,
  cancelManagedBooking,
  type ManagedBooking,
} from "@/lib/booking.functions";
import { Loader2, CalendarX2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/booking/$token")({
  head: () => ({
    meta: buildAppMeta({
      title: "Manage your booking",
      description: "View or cancel your coaching session.",
      path: "/booking",
    }),
  }),
  component: ManageBookingPage,
});

const STATUS_LABEL: Record<string, string> = {
  pending: "Awaiting confirmation",
  confirmed: "Confirmed",
  declined: "Declined",
  cancelled: "Cancelled",
  completed: "Completed",
};

function ManageBookingPage() {
  const { token } = Route.useParams();
  const qc = useQueryClient();
  const fetchBooking = useServerFn(getManagedBooking);
  const cancelBooking = useServerFn(cancelManagedBooking);
  const [busy, setBusy] = useState(false);

  const q = useQuery({
    queryKey: ["managed-booking", token],
    queryFn: async () => (await fetchBooking({ data: { token } })) as ManagedBooking | null,
    retry: 1,
  });

  async function handleCancel() {
    setBusy(true);
    try {
      const res = (await cancelBooking({ data: { token } })) as { ok: boolean; error?: string };
      if (res.ok) {
        toast.success("Booking cancelled");
        qc.invalidateQueries({ queryKey: ["managed-booking", token] });
      } else if (res.error === "already_started") {
        toast.error("This lesson has already started — contact your coach directly.");
      } else {
        toast.error("Couldn't cancel — please try again or contact your coach.");
      }
    } catch {
      toast.error("Couldn't cancel — please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (q.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const b = q.data;
  if (!b) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="p-8 max-w-md text-center">
          <h1 className="text-xl font-semibold">Booking not found</h1>
          <p className="text-sm text-muted-foreground mt-2">
            This link is no longer active. Please contact your coach directly.
          </p>
        </Card>
      </div>
    );
  }

  const isLive = b.status === "pending" || b.status === "confirmed";

  return (
    <div className="min-h-screen bg-muted/30 p-4 md:p-8">
      <div className="max-w-md mx-auto space-y-4 pt-8">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">{b.coach_name}</p>
          <h1 className="text-2xl font-semibold mt-1">Your booking</h1>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Lesson details</h2>
            <Badge variant={isLive ? "default" : "outline"}>
              {STATUS_LABEL[b.status] ?? b.status}
            </Badge>
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <p>
              <span className="font-semibold">When:</span> {fmtBookingDate(b.date)},{" "}
              {fmtSlotTime(b.start)} – {fmtSlotTime(b.end)}
              <span className="text-muted-foreground"> ({b.timezone.replace(/_/g, " ")})</span>
            </p>
            <p>
              <span className="font-semibold">Where:</span> {b.location_name}
              {b.location_city ? ` — ${b.location_city}` : ""}
            </p>
            <p>
              <span className="font-semibold">Booked for:</span> {b.booker_name}
            </p>
          </div>

          {isLive && (
            <div className="mt-6">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="w-full text-destructive" disabled={busy}>
                    <CalendarX2 className="h-4 w-4" />
                    {busy ? "Cancelling…" : "Cancel this booking"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel your lesson?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Your {fmtSlotTime(b.start)} lesson on {fmtBookingDate(b.date)} will be
                      cancelled and {b.coach_name} will be notified.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep booking</AlertDialogCancel>
                    <AlertDialogAction onClick={handleCancel}>Yes, cancel it</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}

          {b.status === "cancelled" && (
            <p className="mt-4 text-sm text-muted-foreground">
              This booking was cancelled. Want a new time?{" "}
              {b.booking_url ? (
                <>
                  <a href={b.booking_url} className="font-medium text-brand-text hover:underline">
                    Click here
                  </a>{" "}
                  to book again.
                </>
              ) : (
                <>Reach out to {b.coach_name} for a new time.</>
              )}
            </p>
          )}
        </Card>

        <p className="text-center text-xs text-muted-foreground pt-4">Powered by CoachQuick</p>
      </div>
    </div>
  );
}
